import { useEffect, useRef, useState, useCallback } from "react";

interface ContentImage {
  src: string;
  alt: string;
}

const contentImages: ContentImage[] = [
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/9f24172f-236d-4a7a-983c-55d05cc3c2bb_compressed_unnamed.webp",
    alt: "사업개요",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/7e36fea0-679e-4aea-90f7-138675cea268_compressed_unnamed.webp",
    alt: "입지환경",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/ce982f59-58a9-4e80-99a1-645457d7cb92_compressed_unnamed.webp",
    alt: "조감도",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/e15b6d15-988b-4b63-aa91-77ae7e96aab9_compressed_unnamed.webp",
    alt: "투시도",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/2009a3aa-2321-4ff8-812f-7dd433b5104f_compressed_unnamed.webp",
    alt: "파주서쪽 교통호재",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/d7542248-54eb-4f03-a783-0e67cc0a6bb4_compressed_2.webp",
    alt: "파주서쪽 의료 및 경제특구",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/18122837-e5ef-48fb-be13-c75b49f932c2_compressed_3.webp",
    alt: "파주서쪽 지하철 및 메디컬클러스터",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/c2fcf853-fb64-4195-8c7a-727ca081b526_compressed_4.webp",
    alt: "파주서쪽 도로확장 및 산업생태계",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/5163b545-8aad-4eda-bc39-5f0025dbece8_compressed_59A.webp",
    alt: "59A 타입 평면도",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/f345eda8-163b-4f7d-a357-16d1c40790ad_compressed_59B.webp",
    alt: "59B 타입 평면도",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/63c15f4c-7838-4836-8830-0a4cccbf52c5_compressed_59C.webp",
    alt: "59C 타입 평면도",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/5330d258-78c0-4402-bbda-55fbe53076cb_compressed_59D.webp",
    alt: "59D 타입 평면도",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/ad32c0f5-1538-4375-8b4e-ae44e84ab7dd_compressed_74a.webp",
    alt: "74A 타입 평면도",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/8c57ba68-475b-48da-90f9-810e0e10770b_compressed_74B.webp",
    alt: "74B 타입 평면도",
  },
  {
    src: "https://storage.helloreaddy.io/project_files/ea88366e-535e-456c-a459-3e8bb0b464d0/57a3b2c0-d4a8-4b41-a101-0a4aab9612ff_compressed_74C.webp",
    alt: "74C 타입 평면도",
  },
];

function FadeUpImage({ src, alt }: ContentImage) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -50px 0px" }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`w-full transition-all duration-1000 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      }`}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-auto object-cover"
      />
    </div>
  );
}

export default function ContentSection() {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const openViewer = useCallback((index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeViewer = useCallback(() => {
    setViewerOpen(false);
    document.body.style.overflow = "";
  }, []);

  const goPrev = useCallback(() => {
    setViewerIndex((prev) => (prev > 0 ? prev - 1 : contentImages.length - 1));
  }, []);

  const goNext = useCallback(() => {
    setViewerIndex((prev) => (prev < contentImages.length - 1 ? prev + 1 : 0));
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!viewerOpen) return;
      if (e.key === "Escape") closeViewer();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [viewerOpen, closeViewer, goPrev, goNext]);

  return (
    <section id="content-section" className="w-full bg-background-50">
      {/* Content Images */}
      <div className="w-full">
        {contentImages.map((img, index) => (
          <div
            key={index}
            className="w-full cursor-pointer"
            onClick={() => openViewer(index)}
          >
            <FadeUpImage src={img.src} alt={img.alt} />
          </div>
        ))}
      </div>

      {/* Image Viewer Modal */}
      {viewerOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          onClick={closeViewer}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 md:px-8 py-4">
            <span className="text-white/70 text-sm">
              {viewerIndex + 1} / {contentImages.length}
            </span>
            <button
              onClick={closeViewer}
              className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer"
              aria-label="닫기"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-close-line text-xl"></i>
              </span>
            </button>
          </div>

          {/* Main image */}
          <div className="flex-1 flex items-center justify-center px-4 md:px-16 relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              className="absolute left-2 md:left-8 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer z-10"
              aria-label="이전"
            >
              <span className="w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-left-s-line text-2xl md:text-3xl"></i>
              </span>
            </button>

            <img
              src={contentImages[viewerIndex].src}
              alt={contentImages[viewerIndex].alt}
              className="max-w-full max-h-[calc(100vh-200px)] object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            <button
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              className="absolute right-2 md:right-8 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer z-10"
              aria-label="다음"
            >
              <span className="w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-2xl md:text-3xl"></i>
              </span>
            </button>
          </div>

          {/* Thumbnails */}
          <div className="px-4 md:px-8 py-4">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {contentImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewerIndex(idx);
                  }}
                  className={`flex-shrink-0 w-16 h-12 md:w-24 md:h-16 rounded overflow-hidden border-2 transition-all cursor-pointer ${
                    idx === viewerIndex
                      ? "border-white"
                      : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}